import DisableIcon from "@mui/icons-material/Block";
import EnableIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useState } from "react";
import { Company } from "src/interfaces/company";
import InitialsAvatar from "src/components/InitialsAvatar";
import StatusPill, { Tone } from "src/components/StatusPill";

interface CompanyItemProps {
  item: Company;
  onClickItem: (item: Company) => void;
  onClickDelete: (item: Company) => void;
  onClickDisable: (item: Company) => void;
  onUpdateSubscription: (id: string, data: any) => Promise<void>;
}

const PLAN_LABEL: Record<string, string> = { STARTER: "Limited", PRO: "Pro", ENTERPRISE: "Enterprise" };
const COMPANY_STATUSES = ["TRIAL_FULL", "TRIAL_LIMITED", "ACTIVE", "EXPIRED", "SUSPENDED", "GRACE"];
const PLAN_TYPES = ["STARTER", "PRO", "ENTERPRISE"];
const TRIAL_STATUSES = ["TRIAL_FULL", "TRIAL_LIMITED"];
const PAID_STATUSES = ["ACTIVE", "GRACE"];
const STATUS_META: Record<string, { label: string; description: string; tone: Tone }> = {
  TRIAL_FULL: {
    label: "Trial full",
    description: "Full trial access. Trial end date is required.",
    tone: "info",
  },
  TRIAL_LIMITED: {
    label: "Trial limited",
    description: "Limited trial access. Trial end date is required.",
    tone: "info",
  },
  ACTIVE: {
    label: "Paid active",
    description: "Paid access is active. Paid end date is required.",
    tone: "success",
  },
  GRACE: {
    label: "Grace period",
    description: "Temporary paid grace access. Paid end date is required.",
    tone: "warning",
  },
  EXPIRED: {
    label: "Expired",
    description: "Read-only access. End dates are optional.",
    tone: "error",
  },
  SUSPENDED: {
    label: "Suspended",
    description: "Account access is blocked. End dates are optional.",
    tone: "error",
  },
};

const initialSubscriptionData = (item: Company) => ({
  status: item.status || "TRIAL_LIMITED",
  trial_end_date: item.trial_end_date?.split("T")[0] || "",
  paid_end_date: item.paid_end_date?.split("T")[0] || "",
  plan_type: item.plan_type || "STARTER",
  custom_price: (item as any).custom_price ?? "",
  custom_currency: (item as any).custom_currency || "",
  custom_price_note: (item as any).custom_price_note || "",
});

type SubscriptionData = ReturnType<typeof initialSubscriptionData>;
type SubscriptionErrors = Partial<Record<keyof SubscriptionData, string>> & {
  general?: string;
};

const getTodayInputValue = () => {
  const today = new Date();
  const offset = today.getTimezoneOffset();
  const localToday = new Date(today.getTime() - offset * 60 * 1000);
  return localToday.toISOString().split("T")[0];
};

const isValidInputDate = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const statusTone = (status: string): Tone => {
  switch (status) {
    case "ACTIVE": return "success";
    case "TRIAL_FULL":
    case "TRIAL_LIMITED": return "info";
    case "GRACE": return "warning";
    case "EXPIRED":
    case "SUSPENDED": return "error";
    default: return "neutral";
  }
};

const CompanyItem = ({
  item,
  onClickItem,
  onClickDelete,
  onClickDisable,
  onUpdateSubscription,
}: CompanyItemProps) => {
  const [dialog, setDialog] = useState<"delete" | "toggle" | "subscription" | null>(null);
  const [subscriptionData, setSubscriptionData] = useState(initialSubscriptionData(item));
  const [subscriptionErrors, setSubscriptionErrors] = useState<SubscriptionErrors>({});
  const [savingSubscription, setSavingSubscription] = useState(false);

  const anyItem = item as any;
  const planLabel = PLAN_LABEL[item.plan_type as string] || item.plan_type || "—";
  const todayInput = getTodayInputValue();
  const isTrialStatus = TRIAL_STATUSES.includes(subscriptionData.status);
  const isPaidStatus = PAID_STATUSES.includes(subscriptionData.status);
  const selectedStatusMeta = STATUS_META[subscriptionData.status] || STATUS_META.TRIAL_LIMITED;

  const actionBtn = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    color: "text.secondary",
    transition: "all 120ms ease",
  };

  const validateSubscription = () => {
    const errors: SubscriptionErrors = {};
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!COMPANY_STATUSES.includes(subscriptionData.status)) {
      errors.status = "Choose a valid company status.";
    }

    if (!PLAN_TYPES.includes(subscriptionData.plan_type)) {
      errors.plan_type = "Choose a valid plan.";
    }

    if (isTrialStatus && !subscriptionData.trial_end_date) {
      errors.trial_end_date = "Trial end date is required for trial status.";
    } else if (subscriptionData.trial_end_date) {
      if (!datePattern.test(subscriptionData.trial_end_date) || !isValidInputDate(subscriptionData.trial_end_date)) {
        errors.trial_end_date = "Enter a valid date.";
      } else if (subscriptionData.trial_end_date < todayInput) {
        errors.trial_end_date = "Trial end date cannot be before today.";
      }
    }

    if (isPaidStatus && !subscriptionData.paid_end_date) {
      errors.paid_end_date = "Paid end date is required for paid or grace status.";
    } else if (subscriptionData.paid_end_date) {
      if (!datePattern.test(subscriptionData.paid_end_date) || !isValidInputDate(subscriptionData.paid_end_date)) {
        errors.paid_end_date = "Enter a valid date.";
      } else if (subscriptionData.paid_end_date < todayInput) {
        errors.paid_end_date = "Paid end date cannot be before today.";
      }
    }

    if (subscriptionData.custom_price !== "" && subscriptionData.custom_price !== null) {
      const price = Number(subscriptionData.custom_price);
      if (Number.isNaN(price)) {
        errors.custom_price = "Enter a valid number.";
      } else if (price < 0) {
        errors.custom_price = "Custom price cannot be negative.";
      }
    }

    if (subscriptionData.custom_currency && subscriptionData.custom_currency.length > 10) {
      errors.custom_currency = "Currency must be 10 characters or less.";
    }

    if (subscriptionData.custom_price_note && subscriptionData.custom_price_note.length > 255) {
      errors.custom_price_note = "Note must be 255 characters or less.";
    }

    return errors;
  };

  const buildSubscriptionPayload = () => {
    const isTrialStatus = TRIAL_STATUSES.includes(subscriptionData.status);
    const isPaidStatus = PAID_STATUSES.includes(subscriptionData.status);

    return {
      status: subscriptionData.status,
      plan_type: subscriptionData.plan_type,
      trial_end_date: isTrialStatus ? subscriptionData.trial_end_date : subscriptionData.trial_end_date || null,
      paid_end_date: isPaidStatus ? subscriptionData.paid_end_date : subscriptionData.paid_end_date || null,
      custom_price: subscriptionData.custom_price === "" ? null : Number(subscriptionData.custom_price),
      custom_currency: subscriptionData.custom_currency || null,
      custom_price_note: subscriptionData.custom_price_note?.trim() || null,
    };
  };

  const updateSubscriptionField = (field: keyof SubscriptionData, value: string) => {
    setSubscriptionData((prev) => ({ ...prev, [field]: value }));
    setSubscriptionErrors((prev) => ({ ...prev, [field]: undefined, general: undefined }));
  };

  const updateSubscriptionStatus = (value: string) => {
    setSubscriptionData((prev) => ({
      ...prev,
      status: value,
      trial_end_date: TRIAL_STATUSES.includes(value) ? prev.trial_end_date : "",
      paid_end_date: PAID_STATUSES.includes(value) ? prev.paid_end_date : "",
    }));
    setSubscriptionErrors({});
  };

  const applyBackendErrors = (error: any) => {
    const errors: SubscriptionErrors = {};
    const backendErrors = error?.errors || error?.data?.errors;

    if (Array.isArray(backendErrors)) {
      backendErrors.forEach((item) => {
        const field = item.field as keyof typeof subscriptionData;
        if (field in subscriptionData) {
          errors[field] = item.message;
        }
      });
    }

    errors.general = error?.message || "Unable to update subscription. Please check the highlighted fields.";
    setSubscriptionErrors(errors);
  };

  const handleSubscriptionSave = async () => {
    const errors = validateSubscription();
    if (Object.keys(errors).length > 0) {
      setSubscriptionErrors(errors);
      return;
    }

    setSavingSubscription(true);
    setSubscriptionErrors({});
    try {
      await onUpdateSubscription(item.company_uuid, buildSubscriptionPayload());
      setDialog(null);
    } catch (error) {
      applyBackendErrors(error);
    } finally {
      setSavingSubscription(false);
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          "& td": { borderBottom: "1px solid #F1F4F8", py: 1.5 },
          "&:last-of-type td": { borderBottom: 0 },
          "& .row-actions": { opacity: { xs: 1, md: 0 }, transition: "opacity 120ms ease" },
          "&:hover .row-actions": { opacity: 1 },
        }}
      >
        {/* Company */}
        <TableCell>
          <Box display="flex" alignItems="center" gap={1.5}>
            <InitialsAvatar name={item.company_name as string} />
            <Box minWidth={0}>
              <Typography
                fontSize="14px"
                fontWeight={700}
                onClick={() => onClickItem(item)}
                sx={{ cursor: "pointer", lineHeight: 1.3, "&:hover": { color: "primary.main" } }}
                noWrap
              >
                {item.company_name}
              </Typography>
              <Typography fontSize="12px" color="text.secondary" noWrap>
                {(item as any).email || "—"}
              </Typography>
            </Box>
          </Box>
        </TableCell>

        {/* Country */}
        <TableCell>
          <Typography fontSize="13px">{(item as any).company_country || "—"}</Typography>
        </TableCell>

        {/* Type */}
        <TableCell>
          <Typography fontSize="13px" color="text.secondary">
            {anyItem.restaurant_type_name || anyItem.restaurant_type || "—"}
          </Typography>
        </TableCell>

        {/* Branches */}
        <TableCell>
          <Typography fontSize="13px" fontWeight={600}>{(item as any).no_of_branches ?? "—"}</Typography>
        </TableCell>

        {/* Plan */}
        <TableCell>
          <StatusPill label={String(planLabel)} tone="primary" dot={false} />
        </TableCell>

        {/* Status */}
        <TableCell>
          {item.disabled ? (
            <StatusPill label="Disabled" tone="neutral" />
          ) : (
            <StatusPill label={item.status || "TRIAL"} tone={statusTone(item.status as string)} />
          )}
        </TableCell>

        {/* Actions */}
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="Manage subscription & pricing">
              <IconButton size="small" onClick={() => { setSubscriptionData(initialSubscriptionData(item)); setSubscriptionErrors({}); setDialog("subscription"); }} sx={{ ...actionBtn, color: "primary.main", "&:hover": { bgcolor: "#EEF2FF" } }}>
                <TuneIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={item.disabled ? "Enable company" : "Disable company"}>
              <IconButton className="row-actions" size="small" onClick={() => setDialog("toggle")} sx={{ ...actionBtn, "&:hover": { bgcolor: "#FEF3C7", color: "#B45309" } }}>
                {item.disabled ? <EnableIcon sx={{ fontSize: 18 }} /> : <DisableIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete company">
              <IconButton className="row-actions" size="small" onClick={() => setDialog("delete")} sx={{ ...actionBtn, "&:hover": { bgcolor: "#FEE2E2", color: "#B91C1C" } }}>
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Confirmation Dialog */}
      <Dialog open={dialog === "delete" || dialog === "toggle"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog === "delete" ? "Delete company?" : item.disabled ? "Enable company?" : "Disable company?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText fontSize="14px">
            {dialog === "delete"
              ? "This will permanently remove the company and all associated data."
              : item.disabled
              ? "This will re-enable the company and restore access."
              : "This will disable the company and restrict access."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog === "delete" ? "error" : "primary"}
            onClick={() => {
              dialog === "delete" ? onClickDelete(item) : onClickDisable(item);
              setDialog(null);
            }}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={dialog === "subscription"} onClose={() => !savingSubscription && setDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "18px" } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box>
              Manage Subscription
              <Typography fontSize="13px" color="text.secondary" fontWeight={400}>{item.company_name}</Typography>
            </Box>
            <StatusPill label={selectedStatusMeta.label} tone={selectedStatusMeta.tone} dot={false} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            {subscriptionErrors.general && (
              <Alert severity="error" sx={{ borderRadius: "10px", alignItems: "center" }}>
                {subscriptionErrors.general}
              </Alert>
            )}

            <Box
              sx={{
                p: 1.5,
                borderRadius: "12px",
                bgcolor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box minWidth={0}>
                <Typography fontSize="13px" fontWeight={800}>{selectedStatusMeta.label}</Typography>
                <Typography fontSize="12px" color="text.secondary">{selectedStatusMeta.description}</Typography>
              </Box>
              <Chip
                label={isTrialStatus ? "Trial" : isPaidStatus ? "Paid" : "Restricted"}
                size="small"
                sx={{ height: 24, borderRadius: "8px", fontWeight: 800, bgcolor: "#EEF2FF", color: "#4338CA" }}
              />
            </Box>

            <TextField select label="Status" fullWidth size="small" value={subscriptionData.status} onChange={(e) => updateSubscriptionStatus(e.target.value)} error={Boolean(subscriptionErrors.status)} helperText={subscriptionErrors.status}>
              <MenuItem value="TRIAL_FULL">Trial Full</MenuItem>
              <MenuItem value="TRIAL_LIMITED">Trial Limited</MenuItem>
              <MenuItem value="ACTIVE">Active (Paid)</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="GRACE">Grace Period</MenuItem>
            </TextField>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label={isTrialStatus ? "Trial End *" : "Trial End"}
                type="date"
                fullWidth
                size="small"
                disabled={isPaidStatus}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayInput }}
                value={subscriptionData.trial_end_date}
                onChange={(e) => updateSubscriptionField("trial_end_date", e.target.value)}
                error={Boolean(subscriptionErrors.trial_end_date)}
                helperText={subscriptionErrors.trial_end_date || (isPaidStatus ? "Cleared when paid access is saved." : " ")}
              />
              <TextField
                label={isPaidStatus ? "Paid End *" : "Paid End"}
                type="date"
                fullWidth
                size="small"
                disabled={isTrialStatus}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayInput }}
                value={subscriptionData.paid_end_date}
                onChange={(e) => updateSubscriptionField("paid_end_date", e.target.value)}
                error={Boolean(subscriptionErrors.paid_end_date)}
                helperText={subscriptionErrors.paid_end_date || (isTrialStatus ? "Cleared when trial access is saved." : " ")}
              />
            </Stack>

            <TextField select label="Plan" fullWidth size="small" value={subscriptionData.plan_type} onChange={(e) => updateSubscriptionField("plan_type", e.target.value)} error={Boolean(subscriptionErrors.plan_type)} helperText={subscriptionErrors.plan_type}>
              <MenuItem value="STARTER">Limited</MenuItem>
              <MenuItem value="PRO">Pro</MenuItem>
              <MenuItem value="ENTERPRISE">Enterprise</MenuItem>
            </TextField>

            <Divider textAlign="left" sx={{ "&::before, &::after": { borderColor: "#EEF1F5" } }}>
              <Typography fontSize="11px" color="text.secondary" fontWeight={700} textTransform="uppercase" letterSpacing="0.05em">
                Custom Pricing
              </Typography>
            </Divider>

            <Stack direction="row" spacing={1.5}>
              <TextField label="Custom price / store" type="number" fullWidth size="small" placeholder="Plan price" value={subscriptionData.custom_price} onChange={(e) => updateSubscriptionField("custom_price", e.target.value)} inputProps={{ min: 0 }} error={Boolean(subscriptionErrors.custom_price)} helperText={subscriptionErrors.custom_price} />
              <TextField select label="Currency" size="small" sx={{ minWidth: 100 }} value={subscriptionData.custom_currency || ""} onChange={(e) => updateSubscriptionField("custom_currency", e.target.value)} error={Boolean(subscriptionErrors.custom_currency)} helperText={subscriptionErrors.custom_currency}>
                <MenuItem value="">—</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="AUD">AUD</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
              </TextField>
            </Stack>
            <TextField label="Override note (internal)" fullWidth size="small" placeholder="e.g. Negotiated enterprise rate" value={subscriptionData.custom_price_note} onChange={(e) => updateSubscriptionField("custom_price_note", e.target.value)} error={Boolean(subscriptionErrors.custom_price_note)} helperText={subscriptionErrors.custom_price_note} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button disabled={savingSubscription} onClick={() => setDialog(null)} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" disabled={savingSubscription} onClick={handleSubscriptionSave} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", minWidth: 128 }}>
            {savingSubscription ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyItem;
